import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ExternalLink, Send, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EvidenceBadge } from "@/components/shared";
import { useAgentStream } from "@/components/AgentRunner";
import { api, errMsg, label } from "@/lib/api";

const MODE_TEXT = {
  direct: "Direct submission — this employer hires on JobPedestal. Your application is delivered to their inbox and you get a verifiable receipt.",
  linkedin_assisted: "LinkedIn-assisted — we prepare your materials; you submit on LinkedIn yourself. We do not automate LinkedIn.",
  external_ats: "Employer ATS — we prepare your materials and open the employer's application page; you submit there.",
};

export function SubmitStep({ app, onDone }) {
  const [approved, setApproved] = useState(false);
  const [ref, setRef] = useState("");
  const [opened, setOpened] = useState(false);
  const open = async () => { const { data } = await api.post(`/applications/${app.application_id}/open-destination`); window.open(data.url, "_blank", "noopener"); setOpened(true); };
  const submit = async () => {
    try { const { data } = await api.post(`/applications/${app.application_id}/submit`, { approved, confirmation_ref: ref }); toast.success(data.evidence === "confirmed" ? "Submitted — receipt issued" : "Submission recorded as candidate-reported"); onDone(data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4" data-testid="apply-submit-step">
      <p className="text-sm text-muted-foreground">{MODE_TEXT[app.mode]}</p>
      {app.mode !== "direct" && (
        <>
          <Button variant="outline" onClick={open} data-testid="apply-open-destination-button"><ExternalLink className="mr-2 h-4 w-4" />Open {app.mode === "linkedin_assisted" ? "LinkedIn" : "employer"} application page</Button>
          <Input placeholder="Confirmation number or email subject (optional)" value={ref} onChange={(e) => setRef(e.target.value)} data-testid="apply-confirmation-ref-input" />
        </>
      )}
      <label className="flex items-start gap-3 rounded-lg border p-3 text-sm">
        <Checkbox checked={approved} onCheckedChange={(v) => setApproved(!!v)} data-testid="apply-approve-checkbox" />
        <span>{app.mode === "direct" ? "I approve submitting this application and materials to the employer." : "I confirm I submitted this application myself on the employer's site."}</span>
      </label>
      <Button disabled={!approved || (app.mode !== "direct" && !opened)} onClick={submit} data-testid="apply-final-submit-button" className="w-full"><Send className="mr-2 h-4 w-4" />{app.mode === "direct" ? "Submit application" : "Record my submission"}</Button>
      {app.mode !== "direct" && !opened && <p className="text-xs text-muted-foreground">Open the application page first. You can also close this and keep the application as prepared (unconfirmed).</p>}
    </div>
  );
}

export default function ApplyDialog({ job, open, onOpenChange }) {
  const [step, setStep] = useState("check");
  const [dups, setDups] = useState([]);
  const [ack, setAck] = useState(false);
  const [cover, setCover] = useState("");
  const [app, setApp] = useState(null);
  const { running, run } = useAgentStream();

  useEffect(() => {
    if (!open) return;
    setStep("check"); setApp(null); setAck(false); setCover("");
    api.get(`/applications/check/${job.job_id}`)
      .then(({ data }) => { setDups(data.duplicates); setStep(data.duplicates.length ? "duplicate" : "prepare"); })
      .catch((e) => { toast.error(errMsg(e)); onOpenChange(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, job.job_id]);
  const start = (o) => onOpenChange(o);
  const draft = async () => { let t = ""; setCover(""); t = await run("cover_letter", { job_id: job.job_id, input: "Draft a cover letter for this role." }); setCover(t); };
  const prepare = async () => {
    try { const { data } = await api.post("/applications", { job_id: job.job_id, cover_letter: cover, acknowledge_duplicate: ack }); setApp(data); setStep("submit"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={start}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-xl" data-testid="apply-dialog">
        <DialogHeader><DialogTitle>AI Apply Assistant · {job.title}</DialogTitle></DialogHeader>
        {step === "check" && <p className="text-sm text-muted-foreground">Checking for existing applications…</p>}
        {step === "duplicate" && (
          <div className="space-y-4" data-testid="apply-duplicate-warning">
            <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"><AlertTriangle className="h-5 w-5 shrink-0" />
              <div><p className="font-semibold">You already have {dups.length} application(s) for this role.</p>
                {dups.map((d) => <p key={d.application_id} className="mt-1">{d.job_snapshot.title} · {d.job_snapshot.employer} · {label(d.status)} · <EvidenceBadge value={d.evidence} /></p>)}</div></div>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={ack} onCheckedChange={(v) => setAck(!!v)} data-testid="apply-duplicate-ack-checkbox" />I understand and want to prepare another application anyway.</label>
            <Button disabled={!ack} onClick={() => setStep("prepare")} data-testid="apply-duplicate-continue-button">Continue</Button>
          </div>
        )}
        {step === "prepare" && (
          <div className="space-y-4" data-testid="apply-prepare-step">
            <p className="text-sm text-muted-foreground">Review your materials. Nothing is sent until you approve.</p>
            <Button variant="outline" size="sm" onClick={draft} disabled={running} data-testid="apply-draft-ai-button"><Wand2 className="mr-2 h-4 w-4" />{running ? "Drafting…" : "Draft cover letter with AI"}</Button>
            <Textarea rows={10} value={cover} onChange={(e) => setCover(e.target.value)} placeholder="Cover letter (optional)" data-testid="apply-cover-letter-input" />
            <Button onClick={prepare} className="w-full" data-testid="apply-prepare-button">Save prepared application</Button>
          </div>
        )}
        {step === "submit" && app && <SubmitStep app={app} onDone={() => setStep("done")} />}
        {step === "done" && <p className="text-sm" data-testid="apply-done-message">Recorded. Track it in Applications — update the status only when you hear back.</p>}
      </DialogContent>
    </Dialog>
  );
}
